import { deepAssign } from './utils.js';

/**
 * @typedef {import('./types').BaseConfigInput} BaseConfigInput
 * @typedef {import('./types').EnvConfigInput} EnvConfigInput
 * @typedef {import('./types').AppConfig} AppConfig
 */

/**
 * Creates the base configuration with common defaults.
 * @param {BaseConfigInput} input - The base configuration input parameters.
 * @returns {AppConfig} The base application configuration.
 */
export const getBaseConfig = ({ rootDomain, subDomain, apiDomain, applicationKey, internalNamespace }) => {
    const internalApplicationKey = applicationKey ?? 'a2l6b21iYQ==';
    /* TODO: add sift beacon keys for other products and envs
        env/default: 17a905f051
        fanduel-casino-canada/default: 17a905f051
        fanduel-casino-usa/default: 17a905f051
        fanduel-poker-canada/default: 836af332f2
        fanduel-poker-canada/prod: ffb5baa171
        fanduel-poker-usa/default: 836af332f2
        fanduel-poker-usa/prod: ffb5baa171
     */
    const domain = subDomain ? `${subDomain}.${rootDomain}` : rootDomain;
    const currentApiDomain = apiDomain ?? domain;

    return {
        rootDomain,
        subDomain,
        domain,
        showAllRegionsToken: '72b19138-c1b9-4397-95ba-14ae652174e7',
        siftBeaconKey: '17a905f051',
        apis: {
            AmplitudeExperimentClient: {
                apiKey: 'server-R8ODqdMihMmGPIimN7QHoMn9KMLoi9Gk'
            },
            AggregationDataClient: {
                protocol: 'https',
                pathPrefix: '/api/aggregation-data',
                host: currentApiDomain,
                timeout: 5000
            },
            GamingFusionEngineClient: {
                protocol: 'https',
                pathPrefix: '/api/gfe/v1',
                host: currentApiDomain,
                timeout: 5000
            },
            CasinoSearchClient: {
                protocol: 'https',
                host: 'casino-search.int.casino.fndl.dev',
                pathPrefix: '/api',
                useCredentials: true,
                timeout: 5000,
                headers: {
                    'x-api-key': '9QnyjhVVIy8KrlTglgAXe5GUxR6T5vY73xHrD5LR'
                }
            },
            CasinoMLSearchClient: {
                protocol: 'https',
                host: currentApiDomain,
                pathPrefix: '/api/search/search',
                useCredentials: true,
                timeout: 5000
            },
            UserContextClient: {
                protocol: 'https',
                pathPrefix: '/api/user-context',
                host: currentApiDomain,
                timeout: 5000
            },
            CustomerDataClient: {
                protocol: 'https',
                host: currentApiDomain,
                pathPrefix: '/api/customer-data',
                useCredentials: true,
                timeout: 5000,
                headers: {
                    'X-Application': applicationKey
                }
            },
            RecentlyPlayedClient: {
                protocol: 'https',
                pathPrefix: '/api/user-activity/recent-games',
                host: currentApiDomain,
                timeout: 5000
            },
            FavoriteGamesClient: {
                protocol: 'https',
                pathPrefix: '/api/user-activity/favourite-games',
                host: currentApiDomain,
                timeout: 5000
            },
            UserActivityClient: {
                protocol: 'https',
                pathPrefix: '/api/user-activity',
                host: currentApiDomain,
                timeout: 5000
            },
            JackpotsClient: {
                protocol: 'https',
                pathPrefix: '/api/games-feed',
                host: currentApiDomain,
                timeout: 5000
            },
            SiteWideJackpotClient: {
                protocol: 'https',
                host: 'api.statePlaceholder.crt.casino.fndl.dev',
                pathPrefix: '',
                useCredentials: true,
                timeout: 5000,
                headers: {
                    'X-Application': applicationKey
                }
            },
            MdsClient: {
                baseUrl: 'mds.statePlaceholder.crt.casino.fndl.dev'
            },
            LauncherClient: {
                protocol: 'https',
                host: `launcher.${currentApiDomain}`,
                pathPrefix: '',
                timeout: 5000
            },
            LoyaltyRewardsClient: {
                protocol: 'https',
                pathPrefix: '/api/rewards',
                host: currentApiDomain,
                timeout: 5000
            },
            LoyaltyCodeClient: {
                protocol: 'https',
                pathPrefix: '/api/loyalty',
                host: currentApiDomain,
                timeout: 5000
            },
            GamesRecommenderClient: {
                protocol: 'https',
                pathPrefix: '/api/games-recommender',
                host: currentApiDomain,
                timeout: 5000
            },
            TransactionHistory: {
                baseUrl: 'https://api.statePlaceholder.crt.casino.fndl.dev'
            },
            CppClient: {
                baseUrl: 'https://usw2-promos-api.fanduel.casino.cert.fndl.dev'
            },
            BanneringToolClient: {
                protocol: 'https',
                host: 'casino-bannering-tool.casino.cert.fndl.dev',
                pathPrefix: '',
                timeout: 1000
            }
        },
        network: {
            InternalAggregationClient: {
                internalNamespace,
                internalService: 'casino-http-aggregation-data',
                internalPort: 4002,
                internalHeaders: {
                    'X-Application': internalApplicationKey
                }
            },
            InternalUserContext: {
                internalNamespace,
                internalService: 'casino-http-user-context',
                internalPort: 4003,
                internalHeaders: {
                    'X-Application': internalApplicationKey
                }
            },
            InternalGamingFusionEngine: {
                internalNamespace,
                internalService: 'gaming-fusion-engine',
                internalPort: 80,
                internalHeaders: {
                    'X-Application': internalApplicationKey
                }
            },
            InternalLoyaltyRewardsClient: {
                internalNamespace,
                internalService: 'loyalty-rewards-api',
                internalPort: 4016,
                internalHeaders: {
                    'X-Application': internalApplicationKey
                }
            },
            InternalCustomerDataClient: {
                internalNamespace,
                internalService: 'casino-http-customer-data',
                internalPort: 4011,
                internalHeaders: {
                    'X-Application': internalApplicationKey
                }
            },
            InternalGamesRecommenderClient: {
                internalNamespace,
                internalService: 'casino-http-games-recommender',
                internalPort: 4014,
                internalHeaders: {
                    'X-Application': internalApplicationKey
                }
            }
        },
        services: {
            amplitudeKey: 'dfdfab544c67bf254b525c7e3928cffa',
            ga: {
                enabled: true,
                code: 'GTM-52MZ6VG'
            },
            datadog: {
                applicationId: '31342310-6205-409a-821c-b0e9b102abfa',
                clientToken: 'pub3e156b7b7e3d9feaa5d0aa4d63040845',
                site: 'datadoghq.com',
                service: 'fanduel-casino-lobby-next',
                sessionSampleRate: 100,
                sessionReplaySampleRate: 0,
                trackBfcacheViews: true,
                trackUserInteractions: true,
                defaultPrivacyLevel: 'mask-user-input',
                trackResources: true,
                trackLongTasks: true,
                useSecureSessionCookie: true
            },
            snowplow: {
                appId: 'casino-web',
                androidAppId: 'casino-android',
                trackerUrl: 'com-fanduel-dev1.collector.snplow.net'
            },
            appStore: {
                sportsbook: {
                    android: 'https://play.google.com/store/apps/details?id=com.fanduel.sportsbook&hl=en_US&gl=US',
                    ios: 'https://apps.apple.com/us/app/fanduel-sportsbook-casino/id1413721906'
                }
            },
            loyalty: {
                clientId: '9ee145b38ada48af9abe1dfeaa53ddc9',
                redirectRoute: '/loyalty/callback',
                url: 'https://onelogin.mymomentumrewards.com/oauth2/auth'
            }
        },
        unifiedModules: {
            pxEnabled: false,
            env: 'cert',
            clientId: '0c72c8f2bb8c7ad6923287c2d3586b9b'
        },
        geolocation: {
            installerKey: 'Pe1pU0EUPZ',
            serviceName: 'casino-next'
        },
        perimeterx: {
            appId: 'YIkzMJ9m'
        },
        log: {
            logRequestContext: true,
            logUnifiedModulesEvents: false,
            logWebVitals: true,
            logResources: true
        },
        publicCache: 0,
        seo: {
            noIndex: true,
            sitemap: true
        }
    };
};

/**
 * Creates the final configuration by merging environment-specific config with base config.
 * @param {EnvConfigInput} envConfig - The environment-specific configuration overrides.
 * @returns {AppConfig} The complete application configuration.
 */
export const getDefaultConfig = (envConfig) => {
    const baseConfig = getBaseConfig({
        rootDomain: envConfig?.rootDomain,
        subDomain: envConfig?.subDomain,
        apiDomain: envConfig?.apiDomain,
        applicationKey: envConfig?.applicationKey,
        internalNamespace: envConfig?.internalNamespace
    });

    return deepAssign(baseConfig, envConfig);
};
